import React, { useEffect, useState } from 'react'
import { Box, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import DateFnsAdapter from '@date-io/date-fns'
import { ThemeButton } from '../theme-button'
import { DateInput } from '../date-input'

/**
 * @callback YesActionCallback
 * @param {object} params - Un objeto que contiene datos adicionales del diálogo.
 * @param {Date | string | undefined} params.from - La fecha de inicio seleccionada, si `fromTo` es `true`.
 * @param {Date | string | undefined} params.to - La fecha de fin seleccionada, si `fromTo` es `true`.
 * @returns {Promise<void> | void} Puede devolver una promesa para operaciones asíncronas.
 */

/**
 * @typedef {object} ConfirmationDialogProps
 * @property {string} [title='Warning:'] - El título que se muestra en la parte superior del diálogo.
 * @property {string} [text='Are you sure you want to proceed?'] - El mensaje o pregunta principal del diálogo.
 * @property {React.ReactNode} [children] - Elementos de React adicionales para renderizar dentro del contenido del diálogo, debajo del texto principal.
 * @property {string} [yesText='Yes'] - El texto para el botón de confirmación (acción "sí").
 * @property {YesActionCallback} [yesAction] - La función a ejecutar al confirmar. Puede ser asíncrona. Recibe un objeto con las fechas `from` y `to` si están activas.
 * @property {boolean} [yesAndClose=true] - Si es `true`, el diálogo se cierra después de que `yesAction` se complete.
 * @property {boolean} [fromTo=false] - Si es `true`, muestra dos campos de selección de fecha para un rango.
 * @property {string} [noText='No'] - El texto para el botón de cancelación (acción "no").
 * @property {string} [noColor='default'] - El color del botón de cancelación.
 * @property {function(): void} [noAction] - La función a ejecutar al cancelar.
 * @property {boolean} [noAndClose=true] - Si es `true`, el diálogo se cierra después de ejecutar `noAction`.
 * @property {function(): void} close - La función para cerrar el diálogo, normalmente pasada desde un componente `Dialog` padre.
 * @property {string} [gestor] - Un texto opcional para mostrar de forma destacada debajo del título, útil para advertencias o nombres.
 * @property {boolean} [awaiting=true] - Si es `true`, el componente esperará a que la promesa devuelta por `yesAction` se resuelva antes de desactivar el estado de carga y cerrar el diálogo.
 */

/**
 * Un componente de diálogo de confirmación flexible (Sí/No) que soporta acciones asíncronas,
 * contenido personalizado y la inclusión opcional de un selector de rango de fechas.
 *
 * Gestiona un estado de carga interno para deshabilitar los botones mientras se procesa la acción de confirmación,
 * proporcionando feedback visual al usuario.
 *
 * **Nota:** Este componente está diseñado para ser renderizado dentro de un contenedor de diálogo, como el componente `Dialog` de Material-UI.
 *
 * @component
 * @example
 * import { Dialog } from '@mui/material';
 *
 * const MyPage = () => {
 * const [open, setOpen] = useState(false);
 *
 * const handleConfirmAction = async (dates) => {
 * console.log('Acción confirmada con fechas:', dates);
 * // Simula una llamada a una API
 * await new Promise(resolve => setTimeout(resolve, 2000));
 * console.log('La operación asíncrona ha terminado.');
 * };
 *
 * return (
 * <>
 * <button onClick={() => setOpen(true)}>Abrir Diálogo</button>
 * <Dialog open={open} onClose={() => setOpen(false)}>
 * <ConfirmationDialog
 * title="Confirmar Acción"
 * text="¿Desea programar esta tarea para el siguiente rango de fechas?"
 * yesText="Programar"
 * noText="Cancelar"
 * fromTo={true}
 * yesAction={handleConfirmAction}
 * close={() => setOpen(false)}
 * />
 * </Dialog>
 * </>
 * );
 * };
 *
 * @param {ConfirmationDialogProps} props - Las props del componente ConfirmationDialog.
 */
export const ConfirmationDialog = ({
  title = 'Warning:',
  text = 'Are you sure you want to proceed?',
  children,
  yesText = 'Yes',
  yesAction = () => {},
  yesAndClose = true,
  fromTo = false,
  noText = 'No',
  noColor = 'default',
  noAction = () => {},
  noAndClose = true,
  close,
  gestor,
  awaiting = true,
}) => {
  const dateFns = new DateFnsAdapter()

  const [loading, setLoading] = useState(false)

  const [from, setFrom] = useState()
  const [to, setTo] = useState()
  const [maxDate, setMaxDate] = useState()

  const handleConfirm = () => {
    setLoading(true)
    if (awaiting) {
      Promise.resolve(yesAction({ from, to }))
        .then(() => {
          setLoading(false)
          if (yesAndClose) {
            close()
          }
        })
        .catch(() => {
          setLoading(false)
        })
    } else {
      yesAction({ from, to })
      setLoading(false)
      if (yesAndClose) {
        close()
      }
    }
  }

  useEffect(() => {
    setMaxDate(dateFns.addDays(dateFns.date(from), 30))
  }, [from]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box p={4}>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="h4" color="primary">
            {title}
          </Typography>
        </Box>
        {gestor && (
          <Box display="flex" alignItems="center" justifyContent="center" mt={2} mb={1}>
            <Typography variant="h5" color="error">
              {gestor}
            </Typography>
          </Box>
        )}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">{text}</Typography>
        {children}
        {fromTo && (
          <Box display="flex" alignItems="center" justifyContent="center" mt={2} mb={1}>
            <Box width={200} m={[0, 1]}>
              <DateInput
                label="from"
                value={from}
                onChange={({ target: { value } }) => {
                  setFrom(value)
                }}
              />
            </Box>

            <Box width={200} m={[0, 1]}>
              <DateInput
                label="to"
                value={to}
                onChange={({ target: { value } }) => {
                  setTo(value)
                }}
                disabled={!from}
                minDate={from}
                maxDate={maxDate}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <ThemeButton
          labelM="small"
          color={noColor}
          onClick={handleConfirm}
          disabled={loading || (fromTo && (!from || !to))}
          loading={loading}
        >
          {yesText}
        </ThemeButton>
        <ThemeButton
          labelM="small"
          color={noColor}
          onClick={() => {
            noAction()
            if (noAndClose) {
              close()
            }
          }}
        >
          {noText}
        </ThemeButton>
      </DialogActions>
    </Box>
  )
}
